import {
	ATTRIBUTES,
	HEPHON,
	VENDORS,
	type Vendor,
	vendorBySlug,
	vsTitle,
} from "@marketing/compare/vendors";
import {
	CompareTable,
	InShort,
	Prose,
	RelatedPages,
} from "@marketing/components/answer";
import {
	marketingHead,
	PageIntro,
	Section,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute, notFound } from "@tanstack/react-router";

export const Route = createFileRoute("/_marketing/compare/$vendor")({
	loader: ({ params }) => {
		const vendor = vendorBySlug(params.vendor);
		if (!vendor) {
			throw notFound();
		}
		return vendor;
	},
	head: ({ loaderData }) =>
		loaderData
			? marketingHead({
					title: pageTitle(loaderData),
					description: loaderData.description,
					path: `/compare/${loaderData.slug}`,
					breadcrumb: ["Compare", loaderData.name],
					faq: faqFor(loaderData),
					updated: loaderData.checked,
				})
			: {},
	component: ComparePage,
});

function ComparePage() {
	const vendor = Route.useLoaderData();
	const { rows, sources } = comparison(vendor);
	const name = vendor.vsName ?? vendor.name;

	return (
		<>
			<PageIntro
				kicker="Compare"
				title={vsTitle(vendor)}
				lead={`${vendor.name}: ${vendor.summary} Hephon Commerce: an AI shopping assistant for supermarkets and grocery retailers in Saudi Arabia and the UAE.`}
			/>

			<InShort updated={vendor.checked}>
				<p>{vendor.inShort}</p>
			</InShort>

			<Section id="choose-title" title="Who should choose which">
				<div className="grid gap-10 sm:grid-cols-2">
					<ChoiceColumn
						title={`Choose ${name} if`}
						items={vendor.chooseThem}
					/>
					<ChoiceColumn
						title="Choose Hephon Commerce if"
						items={vendor.chooseUs}
					/>
				</div>
			</Section>

			<Section
				id="table-title"
				tone="mist"
				title="Feature by feature"
				lead={`Every row comes from ${vendor.name}'s own pages, checked on the date above. "Not stated publicly" means we could not find it, not that it is missing.`}
			>
				<CompareTable
					caption={`Hephon Commerce and ${vendor.name}, compared on ${ATTRIBUTES.length} points. Numbers in the last column refer to the sources below.`}
					columns={["", "Hephon Commerce", vendor.name, "Sources"]}
					rows={rows}
				/>
				<Prose className="mt-10 max-w-none text-[14px]">
					<h3>Sources</h3>
					<ol>
						{sources.map((source) => (
							<li key={source.href}>
								<a
									href={source.href}
									rel={
										source.external ? "noopener" : undefined
									}
								>
									{source.label}
								</a>
							</li>
						))}
					</ol>
				</Prose>
			</Section>

			<Section id="them-title" title={`Where ${name} is stronger`}>
				<Prose className="mx-0">
					<ul>
						{vendor.stronger.map((point) => (
							<li key={point}>{point}</li>
						))}
					</ul>
				</Prose>
			</Section>

			<Section
				id="us-title"
				tone="mist"
				title="Where Hephon Commerce is stronger"
			>
				<Prose className="mx-0">
					<ul>
						{vendor.hephonStronger.map((point) => (
							<li key={point}>{point}</li>
						))}
					</ul>
					<p>
						Hephon Commerce also has limits: access is by
						invitation, through a demo, and there is no public
						price, since each retailer gets a proposal based on its
						stores and channels.
					</p>
				</Prose>
			</Section>

			<Section id="try-title" title="Try it without switching">
				<Prose className="mx-0">
					<p>{vendor.alongside}</p>
					<p>
						Before any call, you can talk to our demo store,{" "}
						<a href="/c/demo">Hephon Market</a>: about 15,000
						products from a real quick-commerce catalog, priced in
						US dollars, that understands Gulf Arabic, Arabizi and
						English. Run the{" "}
						<a href="/best-ai-shopping-assistants#tests">
							five tests
						</a>{" "}
						from our buyer's guide on it, then run the same tests on{" "}
						{vendor.name}.
					</p>
				</Prose>
			</Section>

			<Faq items={faqFor(vendor)} title={`Hephon Commerce or ${name}?`} />
			<RelatedPages links={relatedFor(vendor)} />
			<Closing />
		</>
	);
}

function ChoiceColumn({ title, items }: { title: string; items: string[] }) {
	return (
		<div className="border-(--mk-line) border-t pt-5">
			<h3 className="font-semibold text-[19px] tracking-[-0.02em]">
				{title}
			</h3>
			<ul className="mt-4 list-disc space-y-2 ps-6 text-(--mk-ink-2) text-[16px] leading-relaxed">
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>
	);
}

/** The title stays under 60 characters: long names drop the descriptive tail. */
function pageTitle(vendor: Vendor) {
	const full = `${vsTitle(vendor)}: Grocery AI Comparison (2026)`;
	return full.length <= 60 ? full : `${vsTitle(vendor)} (2026)`;
}

function faqFor(vendor: Vendor) {
	const name = vendor.vsName ?? vendor.name;
	return [
		...vendor.faq,
		{
			q: `Can I try Hephon Commerce without leaving ${name}?`,
			a: `Yes. ${vendor.alongside} You can also talk to our demo store, Hephon Market, at commerce.hephon.ai/c/demo before booking a demo.`,
		},
	];
}

interface Source {
	href: string;
	label: string;
	external: boolean;
}

/** The table rows, each ending with the numbers of its sources, and the numbered source list. */
function comparison(vendor: Vendor) {
	const sources: Source[] = [];
	const numberOf = (href: string) => {
		let index = sources.findIndex((source) => source.href === href);
		if (index === -1) {
			const external = href.startsWith("http");
			sources.push({
				href,
				external,
				label: external
					? href.replace(/^https?:\/\/(www\.)?/, "")
					: `Hephon Commerce: commerce.hephon.ai${href === "/" ? "" : href}`,
			});
			index = sources.length - 1;
		}
		return index + 1;
	};
	const rows = ATTRIBUTES.map(({ key, label }) => {
		const ours = HEPHON.facts[key];
		const theirs = vendor.facts[key];
		return [
			label,
			ours.value,
			theirs.value,
			`[${numberOf(ours.source)}] [${numberOf(theirs.source)}]`,
		];
	});
	return { rows, sources };
}

function relatedFor(vendor: Vendor) {
	const others = [
		...VENDORS.filter(
			(other) =>
				other.slug !== vendor.slug && other.group === vendor.group,
		),
		...VENDORS.filter(
			(other) =>
				other.slug !== vendor.slug && other.group !== vendor.group,
		),
	].slice(0, 4);
	return [
		{
			href: "/best-ai-shopping-assistants",
			label: "Best AI shopping assistants for grocery",
		},
		...others.map((other) => ({
			href: `/compare/${other.slug}`,
			label: vsTitle(other),
		})),
		{ href: "/compare", label: "All comparisons" },
	];
}
